import { Injectable } from '@angular/core';
import {Observable, Subject, throwError} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError, map, switchMap} from "rxjs/operators";
import {environment} from "../../environments/environment";
import {AuthService} from "./auth.service";

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  private apiUrl = environment.api.url;

  token: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) { }

  private reportSource = new Subject<string>();

  report$ = this.reportSource.asObservable();

  updateReport(report: any) {
    this.reportSource.next(report);
  }

  /**
   * Request the on-demand phone-number retrieval for a report ("Unblock").
   * The backend verifies the user can afford it (8 credits) and triggers an
   * async reveal; the numbers arrive later via the APOLLO_PHONE_NUMBER
   * websocket event. Credits are only deducted when a number is delivered.
   */
  unblockPhone(search_id: number, refresh = false): Observable<{ data: any, message: string }> {
    const url = `${this.apiUrl}/search/${search_id}/unblock-phone`;

    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      })
    };

    return this.http.post<any>(url, { refresh }, httpOptions).pipe(
      map(response => {
        if (!response.data) {
          throw new Error('Phone unblock failed: ' + response.message);
        }
        return {
          data: response.data,
          message: response.message
        };
      }),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  shareReport(email: string, search_id: number, message?: string): Observable<{data: any, message: string}> {
    const url = `${this.apiUrl}/search/share`;

    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}` // Use the token here
      })
    };

    let data: any = {
      invite_email: email,
      search_id: search_id
    }

    if (message && message.trim()) {
      data.message = message.trim();
    }

    return this.http.post<any>(url, data, httpOptions).pipe(
      map(response => {
        console.log('[report.service.ts] response', response)

        if (!response.data) {
          // If the success field is false or not present, throw an error
          throw new Error('Report sharing failed: ' + response.message);
        }
        console.log('Share report response:', response);
        // Return whatever part of the response is needed
        return {
          data: response.data,
          message: response.message
        };
      }),
      catchError(error => {
        // Re-throw the error so it can be caught where the service is called
        return throwError(error);
      })
    );
  }

  updatePrompt(search_id: string, assistant_prompt_id: string, prompt_value: string): Observable<{ data: any, message: string }> {
    const url = `${this.apiUrl}/askai/update`;

    // Fetch the token from AuthService
    this.authService.token().subscribe(token => {
      this.token = token;
    });

    // Set the HTTP headers including the Authorization token
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}` // Use the token here
      })
    };

    // Payload for the POST request
    const data = {
      search_id: search_id,
      asisstant_prompt_id: assistant_prompt_id,
      prompt_value: prompt_value
    };

    // Make the HTTP request to the API
    return this.http.post<any>(url, data, httpOptions).pipe(
      map(response => {
        console.log('[report.service.ts] response', response);

        // Check if the response contains the required data
        if (!response.data) {
          // Throw an error if the response is invalid
          throw new Error('Updating prompt failed: ' + response.message);
        }

        console.log('Update prompt response:', response);

        // Return only the necessary data and message
        return {
          data: response.data,
          message: response.message
        };
      }),
      catchError(error => {
        console.error('Update prompt failed:', error);
        // Re-throw the error so it can be caught where the service is called
        return throwError(error);
      })
    );
  }

  refineIcpScore(
    searchCompanyId: number,
    selectedValues: { [key: string]: string },
    formValues: { acceptAdviseDetails?: string, urgencyDetails?: string }
  ): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/search/ipc_refine`;

    // Fetch the token from AuthService
    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`, // Ensure the token is properly fetched
      }),
    };

    /*
    const data = {
      search_company_id: searchCompanyId,
      contact_inboud_lead: selectedValues['inboundLead'] !== 'no',
      contact_confirmed_need: selectedValues['confirmedNeed'] !== 'no',
      contact_validated_need: selectedValues['validateNeed'] !== 'no',
      existing_customer: selectedValues['existingCustomer'] !== 'no',
      existing_partner: selectedValues['partnerEngaged'] !== 'no',
      competitive_product: selectedValues['competitiveProduct'] !== 'no',
      budget: selectedValues['budget'] !== 'no',
      accept_advise: selectedValues['acceptAdvise'] !== 'no',
      urgency: selectedValues['senseUrgency'] !== 'no',
      advice_detail: formValues.acceptAdviseDetails || '',
      urgency_detail: formValues.urgencyDetails || '',
    };
    */

    // Map the selectedValues to numeric values
    const mapValue = (value: string): number => {
      switch (value) {
        case 'yes':
          return 1;
        case 'no':
          return 0;
        case 'na':
          return -1;
        default:
          return -1; // Default to -1 if value is not recognized
      }
    };

    const data = {
      search_company_id: searchCompanyId,
      contact_inboud_lead: mapValue(selectedValues['inboundLead']),
      contact_confirmed_need: mapValue(selectedValues['confirmedNeed']),
      contact_validated_need: mapValue(selectedValues['validateNeed']),
      existing_customer: mapValue(selectedValues['existingCustomer']),
      existing_partner: mapValue(selectedValues['partnerEngaged']),
      competitive_product: mapValue(selectedValues['competitiveProduct']),
      budget: mapValue(selectedValues['budget']),
      accept_advise: mapValue(selectedValues['acceptAdvise']),
      urgency: mapValue(selectedValues['senseUrgency']),
      advice_detail: formValues.acceptAdviseDetails || '',
      urgency_detail: formValues.urgencyDetails || '',
    };

    return this.http.post<any>(url, data, httpOptions).pipe(
      map((response) => {
        console.log('[icp-score.service.ts] response', response);

        if (!response.data) {
          throw new Error('ICP refinement failed: ' + response.message);
        }

        return {
          data: response.data,
          message: response.message,
        };
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  changeCompany(searchId: number, organizationId: string): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/search/switch-company`;

    const data = {
      search_id: searchId,
      organization_id: organizationId,
    };

    // Ensure the token is fetched before making the HTTP request
    return this.authService.token().pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`, // Use the fetched token
          }),
        };

        return this.http.post<any>(url, data, httpOptions).pipe(
          map((response) => {
            // console.log('[change-company.service.ts] response', response);

            if (!response.data) {
              throw new Error('Change company failed: ' + response.message);
            }

            return {
              data: response.data,
              message: response.message,
            };
          }),
          catchError((error) => {
            console.error('[change-company.service.ts] error', error);
            return throwError(() => new Error('Failed to change company.'));
          })
        );
      }),
      catchError((error) => {
        console.error('[change-company.service.ts] Token error', error);
        return throwError(() => new Error('Failed to fetch token.'));
      })
    );
  }

  // ---------------------------------------------------------------------
  // Sales Qualifying Agent (SQL score)
  // ---------------------------------------------------------------------

  private sqlHeaders(): { headers: HttpHeaders } {
    this.authService.token().subscribe(token => {
      this.token = token;
    });

    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      })
    };
  }

  /** Current SQL qualification for the report's active company (null when never run). */
  getSqlQualification(searchId: number, searchCompanyId?: number): Observable<{ enabled?: boolean, qualification: any | null, credit_cost: number }> {
    const params = searchCompanyId ? `?search_company_id=${searchCompanyId}` : '';
    const url = `${this.apiUrl}/search/${searchId}/sql-qualification${params}`;

    return this.http.get<any>(url, this.sqlHeaders()).pipe(
      map(response => response.data),
      catchError(error => throwError(() => error))
    );
  }

  /** Start (or force re-run) the Sales Qualifying Agent. Charges credits; 402 when short. */
  startSqlQualification(searchId: number, force = false, searchCompanyId?: number): Observable<{ qualification: any, already_qualified?: boolean }> {
    const url = `${this.apiUrl}/search/${searchId}/sql-qualification`;
    const body: any = { force };
    if (searchCompanyId) {
      body.search_company_id = searchCompanyId;
    }

    return this.http.post<any>(url, body, this.sqlHeaders()).pipe(
      map(response => response.data),
      catchError(error => throwError(() => error))
    );
  }

  /** Stop the research; the agent finishes with what it gathered (no extra credits). */
  stopSqlQualification(searchId: number, searchCompanyId?: number): Observable<{ qualification: any }> {
    const url = `${this.apiUrl}/search/${searchId}/sql-qualification/stop`;
    const body: any = {};
    if (searchCompanyId) {
      body.search_company_id = searchCompanyId;
    }

    return this.http.post<any>(url, body, this.sqlHeaders()).pipe(
      map(response => response.data),
      catchError(error => throwError(() => error))
    );
  }

  /** Save dialog answers; the score is recomputed server-side without credits. */
  answerSqlQualification(
    searchId: number,
    answers: { q1?: string, q2?: { value_usd: number, offering?: string }, q3?: string, q4?: string, clear?: string[] },
    searchCompanyId?: number
  ): Observable<{ qualification: any }> {
    const url = `${this.apiUrl}/search/${searchId}/sql-qualification/answers`;
    const body: any = { ...answers };
    if (searchCompanyId) {
      body.search_company_id = searchCompanyId;
    }

    return this.http.put<any>(url, body, this.sqlHeaders()).pipe(
      map(response => response.data),
      catchError(error => throwError(() => error))
    );
  }
}
